import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Search } from "lucide-react";
import { api } from "@/lib/api";

export const QuickSearch = ({
  shortcut = false,
  testId = "quick-search",
  placeholder = "Search matters, clients, fee notes…",
}) => {
  const [query, setQuery] = useState("");
  const [groups, setGroups] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!shortcut) return undefined;
    const onKey = (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [shortcut]);

  useEffect(() => {
    const needle = query.trim();
    if (needle.length < 2) {
      setGroups([]);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    const timer = window.setTimeout(async () => {
      try {
        const { data } = await api.get("/search", { params: { q: needle } });
        setGroups(data.groups || []);
      } catch {
        setGroups([]);
      } finally {
        setLoading(false);
      }
    }, 220);
    return () => window.clearTimeout(timer);
  }, [query]);

  const flat = useMemo(
    () => groups.flatMap((group) => group.items.map((item) => ({ ...item, group: group.label }))),
    [groups],
  );

  useEffect(() => {
    setActive(0);
  }, [query]);

  const go = (item) => {
    if (!item) return;
    setOpen(false);
    setQuery("");
    setGroups([]);
    inputRef.current?.blur();
    navigate(item.href);
  };

  const onKeyDown = (event) => {
    if (event.key === "Escape") {
      setOpen(false);
      inputRef.current?.blur();
      return;
    }
    if (!flat.length) return;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActive((a) => (a + 1) % flat.length);
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      setActive((a) => (a - 1 + flat.length) % flat.length);
    }
    if (event.key === "Enter") {
      event.preventDefault();
      go(flat[active]);
    }
  };

  const showPanel = open && query.trim().length >= 2;

  return (
    <div className="relative w-full">
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
      <input
        ref={inputRef}
        value={query}
        onChange={(event) => {
          setQuery(event.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => window.setTimeout(() => setOpen(false), 160)}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        autoComplete="off"
        data-testid={`${testId}-input`}
        className="h-10 w-full border border-slate-200 bg-white pl-9 pr-16 text-[13px] text-slate-900 outline-none transition-colors duration-150 placeholder:text-slate-400 focus:border-slate-900"
      />
      {shortcut && (
        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 border border-slate-200 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-400">
          &#8984; K
        </span>
      )}
      {loading && !shortcut ? (
        <Loader2 className="absolute right-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 animate-spin text-slate-400" />
      ) : null}

      {showPanel && (
        <div
          className="absolute inset-x-0 top-[46px] z-50 max-h-[70vh] overflow-y-auto border border-slate-200 bg-white shadow-xl"
          data-testid={`${testId}-panel`}
        >
          {flat.length === 0 ? (
            <p className="px-4 py-4 text-[12px] text-slate-500" data-testid={`${testId}-empty`}>
              {loading ? "Searching…" : `Nothing found for “${query.trim()}”.`}
            </p>
          ) : (
            groups.map((group) => (
              <div key={group.type} className="border-b border-slate-100 last:border-b-0">
                <p className="label-caps px-4 pb-1.5 pt-3">{group.label}</p>
                {group.items.map((item) => {
                  const index = flat.findIndex((f) => f.id === item.id && f.href === item.href);
                  return (
                    <button
                      key={`${group.type}-${item.id}`}
                      type="button"
                      onMouseDown={(event) => {
                        event.preventDefault();
                        go(item);
                      }}
                      onMouseEnter={() => setActive(index)}
                      data-testid={`${testId}-item-${item.id}`}
                      className={`block w-full px-4 py-2.5 text-left transition-colors duration-150 ${
                        index === active ? "bg-slate-50" : "hover:bg-slate-50"
                      }`}
                    >
                      <span className="block truncate text-[13px] text-slate-900">{item.title}</span>
                      <span className="block truncate text-[11px] text-slate-400">{item.hint}</span>
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};
